import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken = "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

const ParcelScreenFema = () => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [featureCount, setFeatureCount] = useState(0);

  useEffect(() => {
    if (mapRef.current) return;

    // Initialize map
    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [-82.456058, 29.386663],
      zoom: 15,
    });

    // Add navigation controls
    mapRef.current.addControl(new mapboxgl.NavigationControl(), "bottom-right");

    mapRef.current.on("load", async () => {
      try {
        console.log("Map loaded, fetching GeoJSON...");

        // Load GeoJSON file
        const response = await fetch("/assets/parcel/Fema_Flood_Parcel_Custom_2.geojson");
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const geojsonData = await response.json();

        console.log("GeoJSON loaded successfully");
        console.log("Total features:", geojsonData.features?.length);

        setFeatureCount(geojsonData.features?.length || 0);

        // Validate GeoJSON structure
        if (!geojsonData.features || geojsonData.features.length === 0) {
          console.error("No features found in GeoJSON");
          setLoading(false);
          return;
        }

        // Add GeoJSON source
        mapRef.current.addSource("parcels", {
          type: "geojson",
          data: geojsonData,
        });

        console.log("Source added successfully");

        // Add polygon fill layer with FEMA flood zone color coding
        mapRef.current.addLayer({
          id: "parcel-fill",
          type: "fill",
          source: "parcels",
          paint: {
            "fill-color": [
              "match",
              ["get", "parcel_custom_fema_flood"],
              "X", "#afa84cff",      // Green - Minimal Risk (Zone X)
              "AE", "#FF9800",     // Orange - High Risk + BFE (Zone AE)
              "VE", "#E91E63",     // Pink - Coastal High Hazard + BFE (Zone VE)
              "A", "#2196F3",      // Blue - High Risk without BFE (Zone A)
              "AH", "#FF6B6B",     // Light Red - Shallow Flooding
              "AO", "#FFA07A",     // Light Salmon - Sheet Flow
              "V", "#C71585",      // Medium Violet Red - Coastal Flood
              "#ec86f0"            // Gray - Default/Unknown
            ],
            "fill-opacity": 0.65,
          },
        });

        console.log("Fill layer added");

        // Add polygon outline layer
        mapRef.current.addLayer({
          id: "parcel-outline",
          type: "line",
          source: "parcels",
          paint: {
            "line-color": "#333333",
            "line-width": 1,
          },
        });

        console.log("Outline layer added");

        // Add popup on click
        mapRef.current.on("click", "parcel-fill", (e) => {
          const props = e.features[0].properties;

          const formatCurrency = (value) => {
            if (!value || value === "NaN") return "N/A";
            return "$" + Number(value).toLocaleString();
          };

          new mapboxgl.Popup({ closeButton: true, closeOnClick: true })
            .setLngLat(e.lngLat)
            .setHTML(`
              <div style="
                background: #fff;
                padding: 14px;
                border-radius: 8px;
                font-family: 'Inter', -apple-system, sans-serif;
                font-size: 13px;
                min-width: 280px;
                max-width: 320px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              ">
                <h3 style="
                  margin: 0 0 12px 0; 
                  color: #1565C0; 
                  font-size: 15px; 
                  font-weight: 600;
                  border-bottom: 2px solid #E3F2FD;
                  padding-bottom: 8px;
                ">
                  ${props.parcel_custom_address || "Address N/A"}
                </h3>
                
                <div style="display: grid; gap: 6px;">
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">City:</strong>
                    <span>${props.parcel_custom_city || "N/A"}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">County:</strong>
                    <span>${props.parcel_custom_county || "N/A"}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">ZIP:</strong>
                    <span>${props.parcel_custom_szip || "N/A"}</span>
                  </div>
                  
                  <hr style="margin: 8px 0; border: none; border-top: 1px solid #eee;">
                  
                  <div style="display: flex; justify-content: space-between; background: #FFF3E0; padding: 6px; border-radius: 4px;">
                    <strong style="color: #E65100;">Flood Zone:</strong>
                    <span style="font-weight: 600;">${props.parcel_custom_fema_flood || "N/A"}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Flood Risk:</strong>
                    <span>${props.parcel_custom_fema_flo_1 || "N/A"}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">NRI Risk:</strong>
                    <span>${props.parcel_custom_fema_nri_r || "N/A"}</span>
                  </div>
                  
                  <hr style="margin: 8px 0; border: none; border-top: 1px solid #eee;">
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Parcel Size:</strong>
                    <span>${Number(props.parcel_custom_sqft || 0).toLocaleString()} sq ft</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Acres:</strong>
                    <span>${props.parcel_custom_calcacre || "N/A"}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Zoning:</strong>
                    <span>${props.parcel_custom_zoning || "N/A"}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Zone Type:</strong>
                    <span>${props.parcel_custom_zoning_typ || "N/A"}</span>
                  </div>
                  
                  <hr style="margin: 8px 0; border: none; border-top: 1px solid #eee;">
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Land Value:</strong>
                    <span>${formatCurrency(props.parcel_custom_landval)}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Improvement Value:</strong>
                    <span>${formatCurrency(props.parcel_custom_improvval)}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Year Built:</strong>
                    <span>${props.parcel_custom_yearbuilt || "N/A"}</span>
                  </div>
                  
                  <div style="display: flex; justify-content: space-between;">
                    <strong style="color: #555;">Units:</strong>
                    <span>${props.parcel_custom_numunits || "N/A"}</span>
                  </div>
                </div>
              </div>
            `)
            .addTo(mapRef.current);
        });

        // Change cursor on hover
        mapRef.current.on("mouseenter", "parcel-fill", () => {
          mapRef.current.getCanvas().style.cursor = "pointer";
        });

        mapRef.current.on("mouseleave", "parcel-fill", () => {
          mapRef.current.getCanvas().style.cursor = "";
        });

        // Fit map to show all features
        const bounds = new mapboxgl.LngLatBounds();
        
        geojsonData.features.forEach((feature) => {
          if (feature.geometry.type === "MultiPolygon") {
            feature.geometry.coordinates.forEach((polygon) => {
              polygon[0].forEach((coord) => {
                bounds.extend(coord);
              });
            });
          } else if (feature.geometry.type === "Polygon") {
            feature.geometry.coordinates[0].forEach((coord) => {
              bounds.extend(coord);
            });
          }
        });

        mapRef.current.fitBounds(bounds, { padding: 50 });

        setLoading(false);
        console.log("Map setup complete");

      } catch (error) {
        console.error("Error loading parcels:", error);
        setLoading(false);
      }
    });

    // Cleanup on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
      {/* Map Container */}
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />

      {/* Loading Indicator */}
      {loading && (
        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "white",
          padding: "20px 30px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          fontSize: "16px",
          fontWeight: "600",
        }}>
          Loading parcels...
        </div>
      )}

      {/* Feature Count */}
      {!loading && featureCount > 0 && (
        <div style={{
          position: "absolute",
          top: 10,
          left: 10,
          background: "rgba(255, 255, 255, 0.95)",
          padding: "8px 12px",
          borderRadius: "6px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          fontSize: "13px",
          fontWeight: "500",
        }}>
          {featureCount.toLocaleString()} parcels loaded
        </div>
      )}

      {/* Legend */}
      <div style={{
        position: "absolute",
        top: 10,
        right: 10,
        background: "rgba(255, 255, 255, 0.95)",
        padding: "15px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        fontSize: "12px",
        minWidth: "200px",
      }}>
        <div style={{ 
          fontWeight: "bold", 
          marginBottom: "12px", 
          fontSize: "14px",
          borderBottom: "2px solid #1565C0",
          paddingBottom: "6px",
        }}>
          FEMA Flood Zones
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <div style={{ width: 20, height: 20, background: "#4CAF50", borderRadius: "3px" }} />
          <span>X - Minimal Risk</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <div style={{ width: 20, height: 20, background: "#FF9800", borderRadius: "3px" }} />
          <span>AE - High Risk + BFE</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <div style={{ width: 20, height: 20, background: "#E91E63", borderRadius: "3px" }} />
          <span>VE - Coastal + BFE</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <div style={{ width: 20, height: 20, background: "#2196F3", borderRadius: "3px" }} />
          <span>A - High Risk (No BFE)</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: 20, height: 20, background: "#9E9E9E", borderRadius: "3px" }} />
          <span>Unknown</span>
        </div>
      </div>
    </div>
  );
};

export default ParcelScreenFema;






// import React, { useEffect, useRef } from "react";
// import mapboxgl from "mapbox-gl";
// import "mapbox-gl/dist/mapbox-gl.css";

// mapboxgl.accessToken = "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

// const FreshParcelMap = () => {
//   const mapContainer = useRef(null);
//   const mapRef = useRef(null);

//   useEffect(() => {
//     if (mapRef.current) return;

//     mapRef.current = new mapboxgl.Map({
//       container: mapContainer.current,
//       style: "mapbox://styles/mapbox/light-v11",
//       center: [-82.456, 29.387],
//       zoom: 14,
//     });

//     mapRef.current.addControl(new mapboxgl.NavigationControl(), "bottom-right");

//     mapRef.current.on("load", async () => {
//       try {
//         // Load GeoJSON
//         const response = await fetch("/assets/parcel/Fema_Flood_Parcel_Custom_2.geojson");
//         const rawData = await response.json();

//         console.log("Total features loaded:", rawData.features?.length);

//         // Fix stringified coordinates
//         const fixedFeatures = rawData.features.map(feature => {
//           const coords = feature.geometry.coordinates;
          
//           // If coordinates are a string, parse them
//           if (typeof coords === 'string') {
//             try {
//               feature.geometry.coordinates = JSON.parse(coords);
//             } catch (e) {
//               console.error("Failed to parse coordinates:", e);
//               return null;
//             }
//           }
          
//           return feature;
//         }).filter(f => f !== null);

//         const geojsonData = {
//           type: "FeatureCollection",
//           features: fixedFeatures
//         };

//         console.log("Fixed features:", geojsonData.features.length);

//         // Add source
//         mapRef.current.addSource("parcels", {
//           type: "geojson",
//           data: geojsonData,
//         });

//         // Add polygon fill layer with color coding
//         mapRef.current.addLayer({
//           id: "parcel-fill",
//           type: "fill",
//           source: "parcels",
//           paint: {
//             "fill-color": [
//               "match",
//               ["get", "parcel_custom_fema_flood"],
//               "X", "#4CAF50",      // Green - Minimal Risk
//               "AE", "#FF9800",     // Orange - High Risk + BFE
//               "VE", "#E91E63",     // Pink - Coastal + BFE
//               "A", "#2196F3",      // Blue - High Risk (No BFE)
//               "#9E9E9E"            // Gray - Default/Unknown
//             ],
//             "fill-opacity": 0.6,
//           },
//         });

//         // Add outline
//         mapRef.current.addLayer({
//           id: "parcel-outline",
//           type: "line",
//           source: "parcels",
//           paint: {
//             "line-color": "#000",
//             "line-width": 0.5,
//           },
//         });

//         // Add popup on click
//         mapRef.current.on("click", "parcel-fill", (e) => {
//           const props = e.features[0].properties;

//           new mapboxgl.Popup()
//             .setLngLat(e.lngLat)
//             .setHTML(`
//               <div style="padding:10px; font-size:12px;">
//                 <strong>${props.parcel_custom_address || "N/A"}</strong><br/>
//                 <strong>Flood Zone:</strong> ${props.parcel_custom_fema_flood || "N/A"}<br/>
//                 <strong>Risk:</strong> ${props.parcel_custom_fema_nri_r || "N/A"}<br/>
//                 <strong>Size:</strong> ${props.parcel_custom_sqft || "N/A"} sq ft
//               </div>
//             `)
//             .addTo(mapRef.current);
//         });

//         mapRef.current.on("mouseenter", "parcel-fill", () => {
//           mapRef.current.getCanvas().style.cursor = "pointer";
//         });

//         mapRef.current.on("mouseleave", "parcel-fill", () => {
//           mapRef.current.getCanvas().style.cursor = "";
//         });

//         // Fit bounds to data
//         const bounds = new mapboxgl.LngLatBounds();
//         geojsonData.features.forEach(feature => {
//           if (feature.geometry.type === "MultiPolygon") {
//             feature.geometry.coordinates.forEach(polygon => {
//               polygon[0].forEach(coord => {
//                 bounds.extend(coord);
//               });
//             });
//           } else if (feature.geometry.type === "Polygon") {
//             feature.geometry.coordinates[0].forEach(coord => {
//               bounds.extend(coord);
//             });
//           }
//         });

//         mapRef.current.fitBounds(bounds, { padding: 50 });

//       } catch (error) {
//         console.error("Error loading map:", error);
//       }
//     });

//     return () => {
//       if (mapRef.current) mapRef.current.remove();
//     };
//   }, []);

//   return (
//     <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
//       <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
      
//       {/* Legend */}
//       <div style={{
//         position: "absolute",
//         top: 10,
//         right: 10,
//         background: "white",
//         padding: "15px",
//         borderRadius: "8px",
//         boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
//         fontSize: "12px",
//       }}>
//         <div style={{ fontWeight: "bold", marginBottom: "10px" }}>FEMA Flood Zones</div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
//           <div style={{ width: 20, height: 20, background: "#4CAF50" }} />
//           <span>X - Minimal Risk</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
//           <div style={{ width: 20, height: 20, background: "#FF9800" }} />
//           <span>AE - High Risk</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
//           <div style={{ width: 20, height: 20, background: "#E91E63" }} />
//           <span>VE - Coastal</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
//           <div style={{ width: 20, height: 20, background: "#2196F3" }} />
//           <span>A - No BFE</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
//           <div style={{ width: 20, height: 20, background: "#9E9E9E" }} />
//           <span>Unknown</span>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default FreshParcelMap;


// import React, { useEffect, useRef } from "react";
// import mapboxgl from "mapbox-gl";
// import "mapbox-gl/dist/mapbox-gl.css";

// mapboxgl.accessToken = "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

// const FreshParcelMap = () => {
//   const mapContainer = useRef(null);
//   const mapRef = useRef(null);

//   useEffect(() => {
//     if (mapRef.current) return;

//     // Initialize map
//     mapRef.current = new mapboxgl.Map({
//       container: mapContainer.current,
//       style: "mapbox://styles/mapbox/light-v11",
//       center: [-82.456, 29.3867], // Center of your data
//       zoom: 14,
//     });

//     mapRef.current.addControl(new mapboxgl.NavigationControl(), "bottom-right");

//     mapRef.current.on("load", async () => {
//       try {
//         // ✅ LOAD YOUR GEOJSON FILE
//         const response = await fetch("/assets/parcel/Fema_Flood_Parcel_Custom.geojson");
//         const data = await response.json();

//         console.log("Loaded features:", data.features?.length);

//         // ✅ FIX: Parse stringified coordinates
//         data.features = data.features.map(feature => {
//           if (typeof feature.geometry.coordinates === 'string') {
//             try {
//               feature.geometry.coordinates = JSON.parse(feature.geometry.coordinates);
//             } catch (e) {
//               console.warn("Failed to parse coordinates for feature:", feature);
//             }
//           }
//           return feature;
//         });

//         // ✅ ADD SOURCE
//         mapRef.current.addSource("parcels", {
//           type: "geojson",
//           data: data,
//         });

//         // ✅ ADD FILL LAYER WITH COLOR CODING BY FEMA FLOOD ZONE
//         mapRef.current.addLayer({
//           id: "parcel-fill",
//           type: "fill",
//           source: "parcels",
//           paint: {
//             "fill-opacity": 0.5,
//             "fill-color": [
//               "match",
//               ["get", "parcel_custom_fema_flood"],
//               "X", "#4CAF50",   // Green - Minimal Risk
//               "AE", "#FF9800",  // Orange - High Risk + BFE
//               "VE", "#E91E63",  // Pink - Coastal + BFE
//               "A", "#2196F3",   // Blue - High Risk (No BFE)
//               "#9E9E9E"         // Gray - Default/Unknown
//             ],
//           },
//         });

//         // ✅ ADD OUTLINE LAYER
//         mapRef.current.addLayer({
//           id: "parcel-outline",
//           type: "line",
//           source: "parcels",
//           paint: {
//             "line-color": "#333",
//             "line-width": 1,
//           },
//         });

//         // ✅ ADD POPUP ON CLICK
//         mapRef.current.on("click", "parcel-fill", (e) => {
//           const props = e.features[0].properties;

//           new mapboxgl.Popup()
//             .setLngLat(e.lngLat)
//             .setHTML(`
//               <div style="
//                 background:#fff;
//                 padding:12px;
//                 border-radius:8px;
//                 font-family:Inter, sans-serif;
//                 font-size:13px;
//                 width:240px;">
                
//                 <h3 style="margin:0 0 8px 0; color:#1565C0; font-size:14px;">
//                   ${props.parcel_custom_address || "N/A"}
//                 </h3>
                
//                 <hr style="border:none; border-top:1px solid #eee; margin:8px 0;">
                
//                 <p style="margin:4px 0;"><strong>City:</strong> ${props.parcel_custom_city || "N/A"}</p>
//                 <p style="margin:4px 0;"><strong>County:</strong> ${props.parcel_custom_county || "N/A"}</p>
//                 <p style="margin:4px 0;"><strong>Flood Zone:</strong> ${props.parcel_custom_fema_flood || "N/A"}</p>
//                 <p style="margin:4px 0;"><strong>Risk:</strong> ${props.parcel_custom_fema_nri_r || "N/A"}</p>
//                 <p style="margin:4px 0;"><strong>Parcel Size:</strong> ${props.parcel_custom_sqft || "N/A"} sq ft</p>
//                 <p style="margin:4px 0;"><strong>Zoning:</strong> ${props.parcel_custom_zoning || "N/A"}</p>
//               </div>
//             `)
//             .addTo(mapRef.current);
//         });

//         // ✅ CURSOR POINTER
//         mapRef.current.on("mouseenter", "parcel-fill", () => {
//           mapRef.current.getCanvas().style.cursor = "pointer";
//         });

//         mapRef.current.on("mouseleave", "parcel-fill", () => {
//           mapRef.current.getCanvas().style.cursor = "";
//         });

//       } catch (error) {
//         console.error("Error loading parcels:", error);
//       }
//     });

//     return () => {
//       if (mapRef.current) mapRef.current.remove();
//     };
//   }, []);

//   return (
//     <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
//       <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
      
//       {/* ✅ LEGEND */}
//       <div style={{
//         position: "absolute",
//         bottom: 20,
//         left: "50%",
//         transform: "translateX(-50%)",
//         background: "rgba(255, 255, 255, 0.95)",
//         padding: "10px 15px",
//         borderRadius: "8px",
//         boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
//         display: "flex",
//         gap: "15px",
//         fontSize: "12px",
//         fontFamily: "Inter, sans-serif",
//       }}>
//         <span style={{ fontWeight: 600 }}>Flood Zones:</span>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
//           <div style={{ width: 16, height: 16, background: "#4CAF50", borderRadius: 3 }} />
//           <span>X - Minimal</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
//           <div style={{ width: 16, height: 16, background: "#FF9800", borderRadius: 3 }} />
//           <span>AE - High Risk</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
//           <div style={{ width: 16, height: 16, background: "#E91E63", borderRadius: 3 }} />
//           <span>VE - Coastal</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
//           <div style={{ width: 16, height: 16, background: "#2196F3", borderRadius: 3 }} />
//           <span>A - No BFE</span>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default FreshParcelMap;